import {
  useQuery,
  UseQueryResult,
  UseQueryOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import { buildQueryString } from "@mimber-client/utils"
import {
  InvoiceWithRelationsDto,
  ResponseDto,
  QueryParamsDto,
} from "@mimber/schema"
import { memberInvoicesKeys } from "../member-invoices.utils"

/**
 * Hook for fetching a member's invoices
 * Can only be used in mimber-organization app
 *
 * @param memberUuid - Member UUID
 * @param params - Query parameters (pagination, sorting, filtering)
 * @param options - React Query options
 */
export const useMemberInvoices = (
  memberUuid: string,
  params?: QueryParamsDto,
  options?: UseQueryOptions<ResponseDto<InvoiceWithRelationsDto[]>, Error>
): UseQueryResult<ResponseDto<InvoiceWithRelationsDto[]>, Error> => {
  guardAppUsage("mimber-organization", "useMemberInvoices")

  const { server } = useServerContext()

  return useQuery({
    queryKey: memberInvoicesKeys.getMemberInvoices(memberUuid, params),
    queryFn: async () => {
      const response = await server.get<ResponseDto<InvoiceWithRelationsDto[]>>(
        buildQueryString(`members/${memberUuid}/invoices`, params)
      )
      return response.data
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!memberUuid,
    ...options,
  })
}
